import React, { useState } from 'react';
import { X, Upload, Camera } from 'lucide-react';
import api from '../../utils/axios';

const FeedbackModal = ({ isOpen, onClose, onSubmit, formData, handleFormChange, isSubmitting, submitSuccess, colleges = [] }) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);

    if (!isOpen) return null;

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Create local preview
        setPreview(URL.createObjectURL(file));

        // Upload to server
        setUploading(true);
        const data = new FormData();
        data.append('photo', file);

        try {
            const res = await api.post('/student-posts/upload', data);
            handleFormChange({ target: { name: 'photo', value: res.data.url } });
        } catch (err) {
            alert("Photo upload failed");
        } finally {
            setUploading(false);
        }
    };

    const collegeList = (() => {
        if (!colleges) return [];
        if (Array.isArray(colleges)) return colleges;
        try {
            return typeof colleges === 'string' ? JSON.parse(colleges) : colleges;
        } catch(e) {
            return [];
        }
    })();

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl relative overflow-y-auto max-h-[90vh] animate-in zoom-in-95 duration-300">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-400 hover:text-slate-900 transition-colors z-10"
                >
                    <X size={24} />
                </button>

                <div className="p-8 md:p-12">
                    <h3 className="text-3xl font-black uppercase tracking-tighter mb-2">Student <span className="text-[#cc0000]">Feedback</span></h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[4px] mb-8">Tell us about your success & experience</p>

                    {submitSuccess ? (
                        <div className="py-12 text-center animate-in fade-in zoom-in duration-500">
                            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8">
                                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                            </div>
                            <h4 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-4">Submission Received!</h4>
                            <p className="text-slate-500 font-medium leading-relaxed max-w-sm mx-auto">Your story been sent for moderation. It will appear on the wall once approved by the administrator.</p>
                            <button 
                                onClick={onClose} 
                                className="mt-10 bg-slate-900 text-white px-10 py-4 font-black uppercase text-[10px] tracking-[4px] hover:bg-[#cc0000] transition-all"
                            >
                                Close Window
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="space-y-6">
                            {/* Photo Upload */}
                            <div className="flex flex-col items-center mb-10 pb-10 border-b border-slate-100">
                                <div className="relative group">
                                    <div className="w-32 h-32 rounded-full bg-slate-100 overflow-hidden border-4 border-slate-50 shadow-inner flex items-center justify-center">
                                        {preview ? (
                                            <img src={preview} alt="Profile Preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <Camera size={40} className="text-slate-300" />
                                        )}
                                        {uploading && (
                                            <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                                                <div className="w-6 h-6 border-4 border-[#cc0000] border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                    </div>
                                    <label className="absolute bottom-0 right-0 bg-[#cc0000] text-white p-3 rounded-full shadow-lg cursor-pointer hover:bg-slate-900 transition-colors transform group-hover:scale-110">
                                        <Upload size={16} />
                                        <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                    </label>
                                </div>
                                <span className="mt-4 text-[10px] font-black uppercase text-slate-400 tracking-widest">Upload Personal Photo</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Full Name</label>
                                    <input required type="text" name="name" value={formData.name} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="John Doe" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Student Code</label>
                                    <input required type="text" name="studentCode" value={formData.studentCode} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="AS-2026-X" />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Select Your College / School</label>
                                    <select 
                                        required 
                                        name="college" 
                                        value={formData.college || ''} 
                                        onChange={handleFormChange}
                                        className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm text-slate-800"
                                    >
                                        <option value="">Choose Institution...</option>
                                        {(collegeList || []).map((c, i) => (
                                            <option key={i} value={c.name || c}>{c.name || c}</option>
                                        ))}
                                    </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Course / Program</label>
                                    <input required type="text" name="course" value={formData.course} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="e.g. B.Tech Computer Science" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Batch Year</label>
                                    <input required type="text" name="year" value={formData.year} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="e.g. 2024" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Company / Placement</label>
                                    <input required type="text" name="company" value={formData.company} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="Current Workplace / N/A" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Place / City</label>
                                    <input required type="text" name="place" value={formData.place} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="e.g. Jaipur" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Home Address</label>
                                    <input required type="text" name="address" value={formData.address} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="Current Location" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Course Duration</label>
                                    <input required type="text" name="duration" value={formData.duration} onChange={handleFormChange} className="w-full bg-slate-50 border-b-2 border-slate-100 py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" placeholder="e.g. 4 Years" />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Your Feedback / Story</label>
                                <textarea required name="feedback" rows="4" value={formData.feedback} onChange={handleFormChange} className="w-full bg-slate-50 border-2 border-slate-50 p-4 outline-none focus:border-[#cc0000] font-bold text-sm resize-none" placeholder="Share your academic or career experience..."></textarea>
                            </div>

                            <button 
                                disabled={isSubmitting || uploading} 
                                type="submit" 
                                className="w-full bg-slate-900 text-white py-5 font-black uppercase text-[10px] tracking-[4px] hover:bg-[#cc0000] transition-all disabled:opacity-50"
                            >
                                {isSubmitting ? 'Processing Submission...' : 'Send Feedback for Approval'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;

